import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Mic, Stop, Refresh, PlayArrow } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { authFetch, buildApiErrorFromResponse, getApiErrorMessage } from "../utils/apiClient";
import { apiUrl } from "../utils/apiBaseUrl";
import PracticeHistoryPanel from "./communication/PracticeHistoryPanel";

const FLAG_GUIDANCE = {
  LOW_PROMPT_COVERAGE: { label: "Missed key words", hint: "Include more of the target sentence." },
  STARTS_WITH_LOWERCASE: { label: "Capitalization issue", hint: "Start the sentence with a capital letter." },
  MISSING_TERMINAL_PUNCTUATION: { label: "Missing end punctuation", hint: "Finish with a period, question mark, or exclamation mark." },
  TOO_SHORT: { label: "Response too short", hint: "Give a complete sentence with all core ideas." },
  HIGH_FILLER_DENSITY: { label: "Too many filler words", hint: "Pause briefly instead of using filler words." },
  MODERATE_FILLER_DENSITY: { label: "Some filler words", hint: "Slow down and remove extra fillers." },
};

const PACE_COPY = {
  ideal: { label: "On track", hint: "" },
  fast: { label: "A bit fast", hint: "Slow down slightly so each phrase is clear." },
  slow: { label: "A bit slow", hint: "Keep a steadier pace and connect short phrases." },
};

const humanizeFallback = (value) =>
  String(value || "unknown")
    .replace(/_/g, " ")
    .toLowerCase();

const getPaceCopy = (pacingBand) =>
  PACE_COPY[String(pacingBand || "").toLowerCase()] || { label: humanizeFallback(pacingBand), hint: "" };

const getSpeechRecognitionCtor = () =>
  (typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

export default function CommunicationPracticePage() {
  const { getToken } = useAuth();

  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [completedPromptIds, setCompletedPromptIds] = useState([]);
  const [spokenText, setSpokenText] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [listening, setListening] = useState(false);

  const recognitionRef = useRef(null);
  const voiceStartAtRef = useRef(0);
  const speechSupported = useMemo(() => Boolean(getSpeechRecognitionCtor()), []);
  const hasSpokenText = spokenText.trim().length > 0;
  const hasResult = Boolean(result);
  const canEvaluate = Boolean(currentPrompt) && hasSpokenText && !loading;
  const canTryNextSentence = Boolean(currentPrompt) && hasResult && !loading;
  const primaryAction = hasResult ? "next" : hasSpokenText ? "evaluate" : "capture";
  const paceCopy = getPaceCopy(result?.communication_metrics?.pacing_band);
  const remediationHints = useMemo(() => {
    if (!result) return [];
    const hints = [];
    (result.quality_flags || []).forEach((flag) => {
      const hint = FLAG_GUIDANCE[flag]?.hint;
      if (hint && !hints.includes(hint)) hints.push(hint);
    });
    if (paceCopy.hint && !hints.includes(paceCopy.hint)) hints.push(paceCopy.hint);
    return hints.slice(0, 3);
  }, [result, paceCopy.hint]);

  const fetchNextPrompt = useCallback(async (packId, flags = [], completed = []) => {
    if (!packId) return;
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const response = await authFetch(apiUrl("/api/communication-practice/next-prompt"), token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pack_id: packId,
          quality_flags: flags,
          completed_prompt_ids: completed,
        }),
      });
      if (!response.ok) {
        throw await buildApiErrorFromResponse(response, {
          defaultMessage: "Could not load the next sentence. Tap “Try next sentence” to retry.",
        });
      }
      const data = await response.json();
      setCurrentPrompt(data.next_prompt || null);
      setSpokenText("");
      setDurationSeconds(0);
      setResult(null);
    } catch (err) {
      setError(getApiErrorMessage(err, { defaultMessage: "Could not load a practice sentence. Switch packs or tap “Try next sentence”." }));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    let mounted = true;
    const loadPacks = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await getToken();
        const response = await authFetch(apiUrl("/api/communication-practice/packs"), token, { method: "GET" });
        if (!response.ok) {
          throw await buildApiErrorFromResponse(response, {
            defaultMessage: "Could not load practice packs. Refresh and pick a pack to continue.",
          });
        }
        const data = await response.json();
        if (!mounted) return;
        const catalog = Array.isArray(data.packs) ? data.packs : [];
        setPacks(catalog);
        if (catalog.length > 0) {
          setSelectedPackId(catalog[0].id);
        }
      } catch (err) {
        if (!mounted) return;
        setError(getApiErrorMessage(err, { defaultMessage: "Could not load practice packs. Refresh and pick a pack to continue." }));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadPacks();
    return () => {
      mounted = false;
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
      }
    };
  }, [getToken]);

  useEffect(() => {
    if (!selectedPackId) return;
    setCompletedPromptIds([]);
    fetchNextPrompt(selectedPackId, [], []);
  }, [selectedPackId, fetchNextPrompt]);

  const handleSpokenTextChange = useCallback((value) => {
    setSpokenText(value);
    setResult(null);
  }, []);

  const startVoiceCapture = () => {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      setError("Browser speech recognition is unavailable. Type manually or use a supported browser.");
      return;
    }
    setError("");
    setResult(null);

    const recognition = new SpeechRecognitionCtor();
    recognitionRef.current = recognition;
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => {
      setListening(true);
      voiceStartAtRef.current = Date.now();
    };
    recognition.onresult = (event) => {
      const transcript = event?.results?.[0]?.[0]?.transcript || "";
      setSpokenText(String(transcript).trim());
    };
    recognition.onerror = () => {
      setError("Voice capture failed. Try again or type your sentence manually.");
    };
    recognition.onend = () => {
      setListening(false);
      if (voiceStartAtRef.current > 0) {
        setDurationSeconds(Math.max(1, Math.round((Date.now() - voiceStartAtRef.current) / 1000)));
      }
      voiceStartAtRef.current = 0;
    };
    recognition.start();
  };

  const stopVoiceCapture = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const evaluateAttempt = useCallback(async () => {
    if (!currentPrompt) return;
    if (!spokenText.trim()) {
      setError("Speak or type your response before evaluation.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const response = await authFetch(apiUrl("/api/communication-practice/evaluate-turn"), token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pack_id: selectedPackId,
          prompt_id: currentPrompt.id,
          target_sentence: currentPrompt.sentence,
          spoken_text: spokenText,
          duration_seconds: durationSeconds || undefined,
        }),
      });
      if (!response.ok) {
        throw await buildApiErrorFromResponse(response, {
          defaultMessage: "Could not check this response. Keep your text and press “Check response” again.",
        });
      }
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(getApiErrorMessage(err, { defaultMessage: "Could not check this response. Keep your text and press “Check response” again." }));
    } finally {
      setLoading(false);
    }
  }, [currentPrompt, spokenText, getToken, selectedPackId, durationSeconds]);

  const nextPrompt = useCallback(async () => {
    if (!currentPrompt) return;
    if (!result) {
      setError("Check your response before moving to the next sentence.");
      return;
    }
    const nextCompleted = Array.from(new Set([...completedPromptIds, currentPrompt.id]));
    setCompletedPromptIds(nextCompleted);
    await fetchNextPrompt(selectedPackId, result?.quality_flags || [], nextCompleted);
  }, [currentPrompt, result, completedPromptIds, fetchNextPrompt, selectedPackId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.defaultPrevented || event.repeat || event.isComposing) return;

      const key = String(event.key || "").toLowerCase();
      const target = event.target;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && key === "enter") {
        if (canEvaluate) {
          event.preventDefault();
          evaluateAttempt();
        }
        return;
      }

      if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && key === "n") {
        if (isEditableTarget) return;
        if (canTryNextSentence) {
          event.preventDefault();
          nextPrompt();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canEvaluate, canTryNextSentence, evaluateAttempt, nextPrompt]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Communication Practice</Typography>
        <Typography color="text.secondary">
          Voice-first guided speaking drills with instant grammar and fluency coaching.
        </Typography>
      </Stack>

      <PracticeHistoryPanel refreshKey={result?.created_at || result?.score || 0} />

      <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Practice pack</Typography>
              <Select
                fullWidth
                value={selectedPackId}
                onChange={(event) => setSelectedPackId(event.target.value)}
                disabled={loading || packs.length === 0}
              >
                {packs.map((pack) => (
                  <MenuItem key={pack.id} value={pack.id}>
                    {pack.title} ({pack.prompt_count} prompts)
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Target sentence</Typography>
              <Typography sx={{ p: 2, borderRadius: 2, bgcolor: "grey.100" }}>
                {currentPrompt?.sentence || "No sentence is available in this pack yet. Pick another pack, then tap “Try next sentence”."}
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant={primaryAction === "capture" ? "contained" : "outlined"}
                startIcon={listening ? <Stop /> : <Mic />}
                onClick={listening ? stopVoiceCapture : startVoiceCapture}
                disabled={loading || !speechSupported}
              >
                {listening ? "Stop recording" : "Record response"}
              </Button>
              <Button
                variant={primaryAction === "evaluate" ? "contained" : "outlined"}
                startIcon={<PlayArrow />}
                onClick={evaluateAttempt}
                disabled={!canEvaluate}
              >
                Check response
              </Button>
              <Button
                variant={primaryAction === "next" ? "contained" : "outlined"}
                startIcon={<Refresh />}
                onClick={nextPrompt}
                disabled={!canTryNextSentence}
              >
                Try next sentence
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Flow: 1) Record/type response → 2) Check response → 3) Try next sentence.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Next step: {primaryAction === "capture" ? "Record your response." : primaryAction === "evaluate" ? "Check your response." : "Move to the next sentence."}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shortcuts: Ctrl/Cmd+Enter checks response. Alt+N moves to next sentence (outside text inputs).
            </Typography>
            {!result && (
              <Typography variant="caption" color="text.secondary">
                First time here? Record (or type) one sentence, tap “Check response”, then use “Try next sentence” to keep practicing.
              </Typography>
            )}

            {!speechSupported && (
              <Alert severity="info">Voice capture is not supported in this browser. You can still type your response and evaluate it.</Alert>
            )}

            <TextField
              label="Your spoken sentence (captured or typed)"
              multiline
              minRows={3}
              value={spokenText}
              onChange={(event) => handleSpokenTextChange(event.target.value)}
              disabled={loading}
            />

            {result && (
              <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Stack spacing={1.25}>
                  <Typography variant="h6">Feedback</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Chip color="primary" label={`Score ${result.score}/100`} />
                    <Chip label={`Matched target ${(Number(result.coverage_ratio || 0) * 100).toFixed(0)}%`} />
                    <Chip label={`Speaking pace ${paceCopy.label}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Score = overall speaking quality. Matched target = how much key meaning you included. Speaking pace = how easy your speed was to follow.
                  </Typography>
                  <Typography variant="body2"><strong>Better wording:</strong> {result.improved_sentence}</Typography>
                  <Typography variant="body2"><strong>Coach notes:</strong></Typography>
                  <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                    {(result.coaching || []).map((item) => (
                      <li key={item}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </ul>
                  {remediationHints.length > 0 && (
                    <>
                      <Typography variant="body2"><strong>Try this next:</strong></Typography>
                      <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                        {remediationHints.map((hint) => (
                          <li key={hint}>
                            <Typography variant="body2">{hint}</Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {(result.quality_flags || []).length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                      {(result.quality_flags || []).map((flag) => (
                        <Chip key={flag} size="small" label={FLAG_GUIDANCE[flag]?.label || humanizeFallback(flag)} variant="outlined" />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}
